'use client'

import {
    DREComparativoReport,
    DREEvolutionMonth,
    DREReport,
    DREReportItem,
    DREStructure,
    exportDRE,
    getDREEvolution,
    getDREReport,
} from '@/lib/api/financial/financial-accounting-api'
import { CostCenterItem, getCostCenters } from '@/lib/api/financial/cost-center-api'
import { useEffect, useMemo, useState } from 'react'
import {
    MdArrowBack,
    MdChevronRight,
    MdCompareArrows,
    MdExpandMore,
    MdFileDownload,
    MdPrint,
    MdTrendingDown,
    MdTrendingUp,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DREEvolutionChart from './components/dre-evolution-chart'

const formatCurrency = (cents: number) =>
    (Math.abs(cents) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/** Contábil: valor negativo entre parênteses, não com sinal de menos. */
const formatAccounting = (cents: number) =>
    cents < 0 ? `(${formatCurrency(cents)})` : formatCurrency(cents)

const formatSignedPercent = (value: number) =>
    `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`

const formatPercent = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${value.toFixed(1)}%`

const toISODate = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

const getDefaultDates = () => {
    const now = new Date()
    return {
        startDate: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
}

const getPreviousMonthDates = (startDate: string) => {
    const d = new Date(`${startDate}T12:00:00`)
    return {
        compareStartDate: toISODate(new Date(d.getFullYear(), d.getMonth() - 1, 1)),
        compareEndDate: toISODate(new Date(d.getFullYear(), d.getMonth(), 0)),
    }
}

type PeriodShortcut = {
    label: string
    range: () => { startDate: string; endDate: string }
}

const PERIOD_SHORTCUTS: PeriodShortcut[] = [
    {
        label: 'Mês atual',
        range: () => getDefaultDates(),
    },
    {
        label: 'Mês anterior',
        range: () => {
            const now = new Date()
            return {
                startDate: toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
                endDate: toISODate(new Date(now.getFullYear(), now.getMonth(), 0)),
            }
        },
    },
    {
        label: 'Trimestre',
        range: () => {
            const now = new Date()
            const firstMonthOfQuarter = Math.floor(now.getMonth() / 3) * 3
            return {
                startDate: toISODate(new Date(now.getFullYear(), firstMonthOfQuarter, 1)),
                endDate: toISODate(new Date(now.getFullYear(), firstMonthOfQuarter + 3, 0)),
            }
        },
    },
    {
        label: 'Ano',
        range: () => {
            const now = new Date()
            return {
                startDate: toISODate(new Date(now.getFullYear(), 0, 1)),
                endDate: toISODate(new Date(now.getFullYear(), 11, 31)),
            }
        },
    },
    {
        label: '12 meses',
        range: () => {
            const now = new Date()
            return {
                startDate: toISODate(new Date(now.getFullYear(), now.getMonth() - 11, 1)),
                endDate: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
            }
        },
    },
]

function KpiCard({
    title,
    value,
    caption,
    tone = 'neutral',
}: {
    title: string
    value: number
    caption?: string
    tone?: 'neutral' | 'positive' | 'result'
}) {
    const valueTone =
        tone === 'result'
            ? value >= 0
                ? 'text-success'
                : 'text-error'
            : tone === 'positive'
              ? 'text-success'
              : 'text-base-content'

    return (
        <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                {title}
            </p>
            <p className={`mt-1 font-mono text-xl font-bold ${valueTone}`}>
                {formatAccounting(value)}
            </p>
            {caption && (
                <p className="mt-0.5 text-xs text-base-content/60">{caption}</p>
            )}
        </div>
    )
}

/** Linha da cascata do resultado. `level` controla o peso visual. */
function CascadeRow({
    label,
    value,
    percent,
    level = 'item',
    negative = false,
}: {
    label: string
    value: number
    percent?: number | null
    level?: 'item' | 'subtotal' | 'total'
    negative?: boolean
}) {
    const display = negative ? -Math.abs(value) : value
    const styles =
        level === 'total'
            ? 'py-3 text-base font-bold border-t-2 border-base-content/25'
            : level === 'subtotal'
              ? 'py-2 font-semibold border-t border-base-300 bg-base-200/40'
              : 'py-1.5 text-sm'
    const valueTone =
        level === 'total'
            ? display >= 0
                ? 'text-success'
                : 'text-error'
            : negative
              ? 'text-base-content/70'
              : ''

    return (
        <div className={`flex items-center justify-between gap-4 px-2 ${styles}`}>
            <span className={negative ? 'text-base-content/70' : ''}>{label}</span>
            <span className="flex items-baseline gap-3">
                <span className={`w-16 text-right font-mono text-xs text-base-content/50`}>
                    {formatPercent(percent)}
                </span>
                <span className={`w-36 text-right font-mono ${valueTone}`}>
                    {formatAccounting(display)}
                </span>
            </span>
        </div>
    )
}

function StructureCascade({ structure }: { structure: DREStructure }) {
    const pct = (value: number) =>
        structure.netRevenue ? (value / structure.netRevenue) * 100 : null

    return (
        <div className="rounded-xl border border-base-300/60 bg-base-100">
            <div className="border-b border-base-300 px-4 py-3">
                <h3 className="font-bold">Estrutura do resultado</h3>
                <p className="text-xs text-base-content/60">
                    Percentuais sobre a receita líquida
                </p>
            </div>
            <div className="p-2">
                <CascadeRow label="Receita bruta" value={structure.grossRevenue} percent={pct(structure.grossRevenue)} />
                <CascadeRow label="(-) Deduções da receita" value={structure.deductions} percent={pct(structure.deductions)} negative />
                <CascadeRow label="= Receita líquida" value={structure.netRevenue} percent={pct(structure.netRevenue)} level="subtotal" />
                <CascadeRow label="(-) Custos dos serviços e mercadorias" value={structure.costs} percent={pct(structure.costs)} negative />
                <CascadeRow label="= Lucro bruto" value={structure.grossProfit} percent={structure.margins.gross} level="subtotal" />
                <CascadeRow label="(-) Despesas operacionais" value={structure.operatingExpenses} percent={pct(structure.operatingExpenses)} negative />
                {structure.otherOperatingRevenue !== 0 && (
                    <CascadeRow label="(+) Outras receitas operacionais" value={structure.otherOperatingRevenue} percent={pct(structure.otherOperatingRevenue)} />
                )}
                {structure.otherExpenses !== 0 && (
                    <CascadeRow label="(-) Outras despesas" value={structure.otherExpenses} percent={pct(structure.otherExpenses)} negative />
                )}
                <CascadeRow label="= EBITDA" value={structure.ebitda} percent={structure.margins.ebitda} level="subtotal" />
                <CascadeRow label="(-) Depreciação e amortização" value={structure.depreciation} percent={pct(structure.depreciation)} negative />
                <CascadeRow label="= EBIT (resultado operacional)" value={structure.ebit} percent={structure.margins.ebit} level="subtotal" />
                <CascadeRow label="(+) Receitas financeiras" value={structure.financialRevenue} percent={pct(structure.financialRevenue)} />
                <CascadeRow label="(-) Despesas financeiras" value={structure.financialExpenses} percent={pct(structure.financialExpenses)} negative />
                <CascadeRow label="= LAIR (lucro antes do IR)" value={structure.ebt} percent={pct(structure.ebt)} level="subtotal" />
                <CascadeRow label="(-) IRPJ e CSLL" value={structure.taxes} percent={pct(structure.taxes)} negative />
                <CascadeRow label="= LUCRO LÍQUIDO" value={structure.netIncome} percent={structure.margins.net} level="total" />
            </div>
        </div>
    )
}

function TreeRow({
    item,
    depth = 0,
    maxBalance,
    compact = false,
}: {
    item: DREReportItem
    depth?: number
    maxBalance: number
    /** No comparativo cada árvore fica com metade da largura: sem barra. */
    compact?: boolean
}) {
    const [expanded, setExpanded] = useState(true)
    const hasChildren = !!item.children && item.children.length > 0
    const share = maxBalance ? (Math.abs(item.balance) / maxBalance) * 100 : 0

    return (
        <>
            <div
                className={`group flex items-center justify-between gap-3 rounded px-2 py-1.5 text-sm hover:bg-base-200/60 ${hasChildren ? 'font-semibold' : ''}`}
                style={{ paddingLeft: `${depth * 20 + 8}px` }}
            >
                <span className="flex min-w-0 items-center gap-1">
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={() => setExpanded(!expanded)}
                            className="btn btn-ghost btn-xs btn-circle print:hidden"
                            aria-label={expanded ? 'Recolher' : 'Expandir'}
                        >
                            {expanded ? <MdExpandMore size={14} /> : <MdChevronRight size={14} />}
                        </button>
                    ) : (
                        <span className="w-6 print:hidden" />
                    )}
                    <span className="mr-1 font-mono text-xs text-base-content/40">
                        {item.code}
                    </span>
                    <span className="truncate">{item.title}</span>
                </span>
                <span className="flex flex-shrink-0 items-center gap-3">
                    {!compact && (
                        <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-base-300/60 md:block print:hidden">
                            <span
                                className="block h-full rounded-full bg-secondary/60"
                                style={{ width: `${share}%` }}
                            />
                        </span>
                    )}
                    <span className="w-12 text-right font-mono text-xs text-base-content/50">
                        {formatPercent(item.verticalPercent)}
                    </span>
                    <span className={`text-right font-mono ${compact ? 'w-28 text-xs' : 'w-32'}`}>
                        {formatAccounting(item.balance)}
                    </span>
                </span>
            </div>
            {expanded &&
                item.children?.map(child => (
                    <TreeRow
                        key={child.accountId}
                        item={child}
                        depth={depth + 1}
                        maxBalance={maxBalance}
                        compact={compact}
                    />
                ))}
        </>
    )
}

export default function DREReportPage() {
    const navigate = useNavigate()
    const [data, setData] = useState<DREReport | null>(null)
    const [compareData, setCompareData] = useState<DREComparativoReport | null>(null)
    const [evolution, setEvolution] = useState<DREEvolutionMonth[]>([])
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)

    // Filtros avançados
    const [mode, setMode] = useState<'' | 'sintetico' | 'analitico'>('')
    const [costCenterId, setCostCenterId] = useState<number | null>(null)
    const [costCenters, setCostCenters] = useState<CostCenterItem[]>([])
    const [showCompare, setShowCompare] = useState(false)
    const [compareStartDate, setCompareStartDate] = useState('')
    const [compareEndDate, setCompareEndDate] = useState('')
    // O plano SPED tem ~90 contas de resultado; mostrar as zeradas afoga as que
    // têm movimento. Quem precisa do plano inteiro desliga o filtro.
    const [hideEmpty, setHideEmpty] = useState(true)

    useEffect(() => {
        getCostCenters().then(res => {
            if (res.success) setCostCenters(res.result || [])
        })
    }, [])

    useEffect(() => {
        loadData()
    }, [startDate, endDate, mode, costCenterId, showCompare, compareStartDate, compareEndDate])

    const loadData = async () => {
        setLoading(true)
        setCompareData(null)
        try {
            const options: any = {}
            if (mode) options.mode = mode
            if (costCenterId) options.costCenterId = costCenterId
            if (showCompare && compareStartDate && compareEndDate) {
                options.compareStartDate = compareStartDate
                options.compareEndDate = compareEndDate
            }

            const [res, evolutionRes] = await Promise.all([
                getDREReport(startDate, endDate, options),
                getDREEvolution(startDate, endDate, costCenterId || undefined),
            ])

            if (res.success && res.result) {
                if ('current' in res.result) {
                    setCompareData(res.result as DREComparativoReport)
                    setData((res.result as DREComparativoReport).current)
                } else {
                    setData(res.result as DREReport)
                }
            }
            setEvolution(evolutionRes.success ? evolutionRes.result?.months || [] : [])
        } catch {
            toast.error('Erro ao carregar DRE')
        } finally {
            setLoading(false)
        }
    }

    const handleToggleCompare = () => {
        if (!showCompare) {
            const prev = getPreviousMonthDates(startDate)
            setCompareStartDate(prev.compareStartDate)
            setCompareEndDate(prev.compareEndDate)
        }
        setShowCompare(!showCompare)
    }

    const applyShortcut = (shortcut: PeriodShortcut) => {
        const range = shortcut.range()
        setStartDate(range.startDate)
        setEndDate(range.endDate)
    }

    const isAnalitico = mode === 'analitico' || (!mode && data?.mode === 'analitico')
    const structure = data?.structure

    // Base das barras de proporção: a maior linha exibida.
    const maxBalance = useMemo(() => {
        if (!data) return 0
        const collect = (items: DREReportItem[]): number[] =>
            items.flatMap(i => [
                Math.abs(i.balance),
                ...(i.children ? collect(i.children) : []),
            ])
        return Math.max(0, ...collect([...data.income, ...data.expense]))
    }, [data])

    const activeShortcut = PERIOD_SHORTCUTS.find(s => {
        const r = s.range()
        return r.startDate === startDate && r.endDate === endDate
    })

    /** Remove ramos inteiros sem movimento, preservando quem tem filho com saldo. */
    const pruneEmpty = (items: DREReportItem[]): DREReportItem[] =>
        items
            .map(item => ({ ...item, children: item.children ? pruneEmpty(item.children) : undefined }))
            .filter(item => item.balance !== 0 || (item.children && item.children.length > 0))

    const renderSection = (
        title: string,
        rawItems: DREReportItem[],
        total: number,
        colorClass: string,
        compact = false,
    ) => {
        const items = hideEmpty ? pruneEmpty(rawItems) : rawItems
        return (
        <div className="mb-6">
            <h3 className={`mb-2 border-b border-base-300 pb-2 font-bold ${colorClass}`}>
                {title}
            </h3>
            {items.length === 0 && (
                <p className="py-2 text-sm text-base-content/40">
                    Nenhum registro no período
                </p>
            )}
            {isAnalitico
                ? items.map(item => (
                      <TreeRow
                          key={item.accountId}
                          item={item}
                          maxBalance={maxBalance}
                          compact={compact}
                      />
                  ))
                : items.map(item => (
                      <div
                          key={item.accountId}
                          className="flex items-center justify-between gap-3 rounded px-2 py-1.5 text-sm hover:bg-base-200/60"
                      >
                          <span className="truncate">
                              <span className="mr-1 font-mono text-xs text-base-content/40">
                                  {item.code}
                              </span>
                              {item.title}
                          </span>
                          <span className="flex flex-shrink-0 items-center gap-3">
                              <span className="w-14 text-right font-mono text-xs text-base-content/50">
                                  {formatPercent(item.verticalPercent)}
                              </span>
                              <span className="w-32 text-right font-mono">
                                  {formatAccounting(item.balance)}
                              </span>
                          </span>
                      </div>
                  ))}
            <div
                className={`mt-2 flex justify-between border-t border-base-300 px-2 py-2 font-bold ${colorClass}`}
            >
                <span>Total {title}</span>
                <span className="font-mono">{formatAccounting(total)}</span>
            </div>
        </div>
        )
    }

    const renderDRE = (report: DREReport, label?: string) => (
        <div className={label ? 'min-w-0 flex-1' : ''}>
            {label && (
                <h4 className="mb-4 text-center text-sm font-semibold text-base-content/60">
                    {label}
                </h4>
            )}
            {renderSection('RECEITAS', report.income, report.totalIncome, 'text-success', !!label)}
            {renderSection('DESPESAS', report.expense, report.totalExpense, 'text-error', !!label)}
            <div
                className={`flex justify-between border-t-2 border-base-content/30 px-2 py-3 text-lg font-bold ${report.netResult >= 0 ? 'text-success' : 'text-error'}`}
            >
                <span>RESULTADO LÍQUIDO</span>
                <span className="font-mono">{formatAccounting(report.netResult)}</span>
            </div>
        </div>
    )

    // No comparativo são duas árvores lado a lado: a página respira mais larga.
    return (
        <div className={`mx-auto p-4 md:p-6 ${compareData ? 'max-w-7xl' : 'max-w-6xl'}`}>
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <h1 className="text-2xl font-bold">DRE - Demonstrativo de Resultado</h1>
                </div>
                <div className="flex items-center gap-1 print:hidden">
                    <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                            <MdFileDownload size={16} />
                            Exportar
                        </label>
                        <ul
                            tabIndex={0}
                            className="dropdown-content menu z-50 w-44 rounded-box border border-base-300 bg-base-100 p-2 shadow-lg"
                        >
                            <li>
                                <button
                                    onClick={() => {
                                        exportDRE(startDate, endDate, 'xlsx', {
                                            costCenterId: costCenterId || undefined,
                                            mode: mode || undefined,
                                        }).catch(() => toast.error('Erro ao exportar'))
                                    }}
                                >
                                    Excel (.xlsx)
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        exportDRE(startDate, endDate, 'pdf', {
                                            costCenterId: costCenterId || undefined,
                                            mode: mode || undefined,
                                        }).catch(() => toast.error('Erro ao exportar'))
                                    }}
                                >
                                    PDF
                                </button>
                            </li>
                        </ul>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>
                        <MdPrint size={18} />
                    </button>
                </div>
            </div>

            {/* Atalhos de período */}
            <div className="mb-3 flex flex-wrap gap-2 print:hidden">
                {PERIOD_SHORTCUTS.map(shortcut => (
                    <button
                        key={shortcut.label}
                        className={`btn btn-xs ${activeShortcut?.label === shortcut.label ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                        onClick={() => applyShortcut(shortcut)}
                    >
                        {shortcut.label}
                    </button>
                ))}
            </div>

            {/* Filtros */}
            <div className="mb-6 flex flex-wrap items-end gap-3 print:hidden">
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Início</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Fim</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Modo</span>
                    </label>
                    <select
                        className="select select-bordered select-sm"
                        value={mode}
                        onChange={e => setMode(e.target.value as any)}
                    >
                        <option value="">Padrão</option>
                        <option value="sintetico">Sintético</option>
                        <option value="analitico">Analítico</option>
                    </select>
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Centro de Custo</span>
                    </label>
                    <select
                        className="select select-bordered select-sm"
                        value={costCenterId || ''}
                        onChange={e =>
                            setCostCenterId(e.target.value ? parseInt(e.target.value, 10) : null)
                        }
                    >
                        <option value="">Todos</option>
                        {costCenters.map(cc => (
                            <option key={cc.id} value={cc.id}>
                                {cc.code} - {cc.name}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    className={`btn btn-sm gap-1 ${showCompare ? 'btn-secondary' : 'btn-outline'}`}
                    onClick={handleToggleCompare}
                >
                    <MdCompareArrows size={16} />
                    Comparar
                </button>
                <label className="label cursor-pointer gap-2 pb-2">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={hideEmpty}
                        onChange={e => setHideEmpty(e.target.checked)}
                    />
                    <span className="label-text text-xs">Ocultar contas sem movimento</span>
                </label>
            </div>

            {showCompare && (
                <div className="mb-6 flex items-end gap-3 rounded-lg bg-base-200/50 p-3 print:hidden">
                    <span className="text-sm font-medium text-base-content/60">
                        Período anterior:
                    </span>
                    <div className="form-control">
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={compareStartDate}
                            onChange={e => setCompareStartDate(e.target.value)}
                        />
                    </div>
                    <div className="form-control">
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={compareEndDate}
                            onChange={e => setCompareEndDate(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {loading && (
                <div className="flex justify-center py-20">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {!loading && data && (
                <div className="space-y-6">
                    {/* KPIs */}
                    {structure && (
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <KpiCard
                                title="Receita líquida"
                                value={structure.netRevenue}
                                caption={
                                    structure.deductions
                                        ? `Bruta ${formatCurrency(structure.grossRevenue)} − deduções`
                                        : 'Sem deduções no período'
                                }
                                tone="positive"
                            />
                            <KpiCard
                                title="Lucro bruto"
                                value={structure.grossProfit}
                                caption={`Margem ${formatPercent(structure.margins.gross)}`}
                                tone="result"
                            />
                            <KpiCard
                                title="EBITDA"
                                value={structure.ebitda}
                                caption={`Margem ${formatPercent(structure.margins.ebitda)}`}
                                tone="result"
                            />
                            <KpiCard
                                title="Lucro líquido"
                                value={structure.netIncome}
                                caption={`Margem ${formatPercent(structure.margins.net)}`}
                                tone="result"
                            />
                        </div>
                    )}

                    {structure && !structure.available && (
                        <div className="alert alert-warning py-2 text-sm">
                            O plano de contas desta empresa não segue a numeração SPED, então a
                            abertura por custo, despesa operacional e resultado financeiro fica
                            aproximada. O lucro líquido continua correto.
                        </div>
                    )}

                    {/* Evolução mensal */}
                    {!compareData && (
                        <div className="rounded-xl border border-base-300/60 bg-base-100 p-4 print:hidden">
                            <div className="mb-3 flex items-center justify-between">
                                <div>
                                    <h3 className="font-bold">Evolução no período</h3>
                                    <p className="text-xs text-base-content/60">
                                        Receita líquida, custos e despesas, e lucro líquido por mês
                                    </p>
                                </div>
                                {evolution.length > 1 && (
                                    <MonthlyTrend months={evolution} />
                                )}
                            </div>
                            <DREEvolutionChart months={evolution} />
                        </div>
                    )}

                    {/* Cascata */}
                    {structure && !compareData && <StructureCascade structure={structure} />}

                    {/* Detalhamento por conta */}
                    <div className="card bg-base-100 shadow print:shadow-none">
                        <div className="card-body p-6">
                            <div className="mb-6 text-center print:mb-4">
                                <h2 className="text-lg font-bold">
                                    Demonstrativo de Resultado do Exercício
                                </h2>
                                <p className="text-sm text-base-content/60">
                                    {new Date(`${startDate}T12:00:00`).toLocaleDateString('pt-BR')} a{' '}
                                    {new Date(`${endDate}T12:00:00`).toLocaleDateString('pt-BR')}
                                    {mode === 'sintetico' && ' (Sintético)'}
                                    {mode === 'analitico' && ' (Analítico)'}
                                    {costCenterId &&
                                        ` - Centro de Custo: ${costCenters.find(c => c.id === costCenterId)?.name || ''}`}
                                </p>
                            </div>

                            {compareData ? (
                                <>
                                    <div className="flex gap-6">
                                        {renderDRE(
                                            compareData.current,
                                            `Atual (${new Date(`${startDate}T12:00:00`).toLocaleDateString('pt-BR')} - ${new Date(`${endDate}T12:00:00`).toLocaleDateString('pt-BR')})`,
                                        )}
                                        <div className="divider divider-horizontal" />
                                        {renderDRE(
                                            compareData.previous,
                                            `Anterior (${new Date(`${compareStartDate}T12:00:00`).toLocaleDateString('pt-BR')} - ${new Date(`${compareEndDate}T12:00:00`).toLocaleDateString('pt-BR')})`,
                                        )}
                                    </div>
                                    <div className="mt-6 border-t-2 border-base-content/20 pt-4">
                                        <h3 className="mb-3 font-bold">Variação</h3>
                                        <div className="grid grid-cols-3 gap-4">
                                            {[
                                                {
                                                    title: 'Receitas',
                                                    value: compareData.variance.totalIncome,
                                                    percent: compareData.variance.totalIncomePercent,
                                                    goodWhenPositive: true,
                                                },
                                                {
                                                    title: 'Despesas',
                                                    value: compareData.variance.totalExpense,
                                                    percent: compareData.variance.totalExpensePercent,
                                                    goodWhenPositive: false,
                                                },
                                                {
                                                    title: 'Resultado',
                                                    value: compareData.variance.netResult,
                                                    percent: compareData.variance.netResultPercent,
                                                    goodWhenPositive: true,
                                                },
                                            ].map(item => {
                                                const good = item.goodWhenPositive
                                                    ? item.value >= 0
                                                    : item.value <= 0
                                                return (
                                                    <div
                                                        key={item.title}
                                                        className="rounded-lg border border-base-300/60 p-3"
                                                    >
                                                        <div className="text-xs text-base-content/60">
                                                            {item.title}
                                                        </div>
                                                        <div
                                                            className={`font-mono text-base font-bold ${good ? 'text-success' : 'text-error'}`}
                                                        >
                                                            {formatAccounting(item.value)}
                                                        </div>
                                                        <div className="flex items-center gap-1 text-xs text-base-content/60">
                                                            {item.value >= 0 ? (
                                                                <MdTrendingUp size={14} />
                                                            ) : (
                                                                <MdTrendingDown size={14} />
                                                            )}
                                                            {formatSignedPercent(item.percent)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                renderDRE(data)
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

/** Variação do lucro líquido entre o primeiro e o último mês da série. */
function MonthlyTrend({ months }: { months: DREEvolutionMonth[] }) {
    const first = months[0]
    const last = months[months.length - 1]
    const delta = last.netIncome - first.netIncome
    const percent = first.netIncome
        ? (delta / Math.abs(first.netIncome)) * 100
        : null

    return (
        <div className="text-right">
            <div
                className={`flex items-center justify-end gap-1 font-mono text-sm font-bold ${delta >= 0 ? 'text-success' : 'text-error'}`}
            >
                {delta >= 0 ? <MdTrendingUp size={16} /> : <MdTrendingDown size={16} />}
                {formatAccounting(delta)}
            </div>
            <p className="text-xs text-base-content/60">
                lucro líquido {percent === null ? 'no período' : formatSignedPercent(percent)}
            </p>
        </div>
    )
}
